import React, { useEffect, useState } from "react";
import { Stack, IconButton, FontIcon } from "@fluentui/react";
import SummaryTable from "./SummaryTable";
import ActionButton from "./ActionButton";
import { IReport_Merchant, IReport_Receipt } from "../../../shared/interfaces/IReceipAnalyzerEntities";
import { useAppContext } from "./AppContext";
import { useSearchParams } from "react-router";
import { useNavigate } from 'react-router';
import SConsciousApiService from "../services/SConsciousApiService";
import ProgressBar from './ProgressBar';

interface MerchantProps {
  merchant?: IReport_Merchant;
}

export default function Merchant({ merchant }: MerchantProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {appData, setAppData} = useAppContext();
    const _api = new SConsciousApiService();
    const [social, setSocial] = useState<{ friends: any[], strangers: number } | null>(null);
    const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
    const [editingAddress, setEditingAddress] = useState(false);
    const [addressForm, setAddressForm] = useState({ street_address: '', city: '', postal_code: '', country: '' });
    const [saving, setSaving] = useState(false);

    if (!merchant) {
        const merchantId = searchParams.get("merchantId");
        merchant = appData.data.reportMerchants?.find((m) => m.merchant_id === merchantId);
    }

    useEffect(() => {
        if (!merchant?.merchant_id || !appData.user.userId) return;
        _api.getMerchantSocial(appData.user.userId, merchant.merchant_id).then(data => {
            setSocial(data);
            if (data.likedFriendIds?.length) {
                setLikedIds(new Set(data.likedFriendIds));
            }
        });
    }, [merchant?.merchant_id, appData.user.userId]);

    if (!appData.dataFetched) {
        return <ProgressBar label="Loading" progress={20} />;
    }

    if (!merchant) {
        navigate('/');
        return null;
    }

    return (
        <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
            <div className="merchant-summary">
                <br/>
                <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto', padding: '18px 0' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, margin: 6, letterSpacing: 1 }}>
                    {merchant?.name || 'Unknown Merchant'}
                    </div>
                    {merchant?.owning_corporation && (
                        <div style={{ marginBottom: 8 }}>
                            <ActionButton label={merchant.owning_corporation} style={{ margin: 0 }} onClick={() => navigate(`/Corporation?corporationId=${encodeURIComponent(merchant!.owning_corporation!)}`)} />
                        </div>
                    )}
                    {editingAddress ? (
                        <div style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 12, padding: '14px 16px', width: '85%', maxWidth: 340, margin: '8px auto 0' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, opacity: 0.6 }}>
                                Edit address
                            </div>
                            <input
                                className="darkInput"
                                placeholder="Street address"
                                value={addressForm.street_address}
                                onChange={e => setAddressForm(f => ({ ...f, street_address: e.target.value }))}
                                style={{ width: '100%', marginBottom: 6, fontFamily: 'inherit', boxSizing: 'border-box' }}
                            />
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input
                                    className="darkInput"
                                    placeholder="City"
                                    value={addressForm.city}
                                    onChange={e => setAddressForm(f => ({ ...f, city: e.target.value }))}
                                    style={{ flex: 2, fontFamily: 'inherit' }}
                                />
                                <input
                                    className="darkInput"
                                    placeholder="Postal code"
                                    value={addressForm.postal_code}
                                    onChange={e => setAddressForm(f => ({ ...f, postal_code: e.target.value }))}
                                    style={{ flex: 1, fontFamily: 'inherit' }}
                                />
                            </div>
                            <input
                                className="darkInput"
                                placeholder="Country"
                                value={addressForm.country}
                                onChange={e => setAddressForm(f => ({ ...f, country: e.target.value }))}
                                style={{ width: '100%', marginTop: 6, fontFamily: 'inherit', boxSizing: 'border-box' }}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                                <button
                                    disabled={saving || !addressForm.street_address || !addressForm.city || !addressForm.postal_code}
                                    style={{
                                        fontFamily: 'inherit',
                                        flex: 1,
                                        background: '#000',
                                        color: '#ffcc00',
                                        border: 'none',
                                        borderRadius: 20,
                                        padding: '8px 0',
                                        opacity: saving ? 0.35 : 1,
                                        cursor: !saving && addressForm.street_address && addressForm.city && addressForm.postal_code ? 'pointer' : 'not-allowed',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={async () => {
                                        if (!merchant?.merchant_id) return;
                                        setSaving(true);
                                        const result = await _api.updateMerchantAddress(merchant.merchant_id, addressForm);
                                        setSaving(false);
                                        if (result.success && result.merchant) {
                                            const updated = appData.data.reportMerchants?.map(m =>
                                                m.merchant_id === merchant!.merchant_id
                                                    ? { ...m, street_address: result.merchant.street_address, city: result.merchant.city, postal_code: result.merchant.postal_code, country: result.merchant.country, longitude: result.merchant.longitude, latitude: result.merchant.latitude }
                                                    : m
                                            );
                                            setAppData({ ...appData, data: { ...appData.data, reportMerchants: updated } });
                                        }
                                        setEditingAddress(false);
                                    }}
                                >
                                    <FontIcon iconName="Save" style={{ fontSize: '1rem' }} />
                                </button>
                                <button
                                    style={{
                                        fontFamily: 'inherit',
                                        flex: '0 0 calc(25% - 12px)',
                                        background: 'rgba(0,0,0,0.06)',
                                        color: '#000',
                                        border: 'none',
                                        borderRadius: 20,
                                        padding: '8px 0',
                                        cursor: saving ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    disabled={saving}
                                    onClick={() => setEditingAddress(false)}
                                >
                                    <FontIcon iconName="Cancel" style={{ fontSize: '1rem' }} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginBottom: 2, fontSize: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <span>
                                    {merchant?.street_address && <span>{merchant.street_address}</span>}
                                    {merchant?.street_address && <br />}
                                    {merchant?.city || ''} {merchant?.postal_code || ''}
                                    {(merchant?.country || merchant?.merchant_region) && (
                                        <span> - {merchant?.country || merchant?.merchant_region}</span>
                                    )}
                                </span>
                                <button
                                    onClick={() => {
                                        setAddressForm({
                                            street_address: merchant?.street_address || '',
                                            city: merchant?.city || '',
                                            postal_code: merchant?.postal_code || '',
                                            country: merchant?.country || '',
                                        });
                                        setEditingAddress(true);
                                    }}
                                    style={{ fontFamily: 'inherit', background: 'rgba(0,0,0,0.10)', color: '#000', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    title="Edit address"
                                >
                                    <FontIcon iconName="Edit" style={{ fontSize: '0.75rem' }} />
                                </button>
                            </div>
                        </div>
                    )}
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                        <span style={{ marginLeft: 8 }}>{merchant?.phone_number}</span>
                    </div>
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                        <strong>Total Amount: {merchant?.merchant_total_amount?.toFixed(2) || '0.00'} {appData.user.currency || ''}</strong> 
                    </div>
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                        <strong>First Visit:</strong> {merchant?.merchant_first_visit || ''}
                    </div>
                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                        <strong>Last Visit:</strong> {merchant?.merchant_last_visit || ''}
                    </div>
                </div>
                <div>
                    <br/>
                    <strong style={{ fontSize: 14 }}>Receipts for this Merchant:</strong>
                    <br/>
                    <br/>
                    <Stack horizontalAlign="center" style={{ margin: 'auto', width: '90%' }}>
                    <SummaryTable
                        data={(appData?.data?.reportReceipts ?? []).filter(receipt => receipt.merchant_id === merchant?.merchant_id).map((receipt, idx) => ({ id: idx, ...receipt }))}
                        columns={[
                        { 
                            key: "transaction_date", 
                            label: "Date", 
                            width: "50%", 
                            total: false, 
                            render: (_: any, item: IReport_Receipt) => (
                                <span className='table-cell-content' onClick={() => navigate(`/Receipt?receiptId=${item.receipt_id}`)} style={{ cursor: 'pointer' }}>{item.transaction_date}</span>
                            ),
                        },
                        { key: "transaction_time", label: "Time", width: "33%", total: false },
                        { key: "total_amount", label: "Total", width: "22%", total: true },
                        ]}
                        filterKeys={[]}
                        header={true}
                        total={true}
                    />
                    </Stack>
                    <br/>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '12px auto 0', padding: '14px 0' }}>
                    <strong style={{ fontSize: 13, display: 'block', margin: '0 0 6px 6px' }}>Also shopping here</strong>
                    {social && social.friends.length > 0 ? (
                        <Stack horizontalAlign="center" style={{ margin: '8px auto 0', width: '90%' }}>
                            <SummaryTable
                                data={social.friends.map((f: any, idx: number) => ({ id: idx, ...f }))}
                                columns={[
                                    { key: 'friend_name', label: 'Friend', width: '50%', total: false,
                                        render: (_: any, f: any) => <span className="table-cell-content">{f.friend_name || f.friend_email}</span>
                                    },
                                    { key: 'last_visit', label: 'Last visit', width: '30%', total: false,
                                        render: (_: any, f: any) => <span className="table-cell-content">{f.last_visit || '—'}</span>
                                    },
                                    { key: 'friend_user_id', label: '', width: '20%', total: false,
                                        render: (_: any, f: any) => (
                                            <IconButton
                                                iconProps={{ iconName: 'Like' }}
                                                title={likedIds.has(f.friend_user_id) ? 'Liked' : 'Send a like'}
                                                ariaLabel="Like"
                                                disabled={likedIds.has(f.friend_user_id)}
                                                onClick={() => {
                                                    _api.sendLike(appData.user.userId, f.friend_user_id, merchant?.name || '', merchant?.merchant_id || '');
                                                    setLikedIds(prev => new Set(prev).add(f.friend_user_id));
                                                }}
                                                styles={{
                                                    root: { backgroundColor: 'transparent', borderRadius: '50%', opacity: likedIds.has(f.friend_user_id) ? 0.45 : 1, height: 20, width: 20, minWidth: 20, padding: 0 },
                                                    rootHovered: { backgroundColor: 'transparent' },
                                                    rootPressed: { backgroundColor: 'transparent' },
                                                    rootDisabled: { backgroundColor: 'transparent', opacity: 0.45 },
                                                    flexContainer: { height: 20, width: 20, overflow: 'hidden' },
                                                    icon: { color: '#ffcc00', fontSize: 12, lineHeight: '20px', height: 20, margin: 0 },
                                                    iconDisabled: { color: '#000000' },
                                                }}
                                            />
                                        )
                                    },
                                ] as any}
                                filterKeys={[]}
                                header={true}
                                total={false}
                            />
                        </Stack>
                    ) : (
                        <div style={{ fontSize: 12, marginTop: 6, opacity: 0.6 }}>No friends have visited this store yet.</div>
                    )}
                    <div style={{ fontSize: 12, marginTop: 8, marginLeft: 6, opacity: 0.65 }}>
                        {social && social.strangers > 0
                        ? `+ ${social.strangers} ${social.strangers === 1 ? 'stranger has' : 'strangers have'} been here`
                            : 'No other visitors yet.'}
                    </div>
                </div>
                <br/>
            </div>
        </Stack>
    );
}